// @flow

/**
 * Chat utility formatters
 * Pure functions for formatting durations, normalizing markdown, and text checks
 */

/**
 * Formats a duration in milliseconds to a human-readable string
 * @param {number} ms - Duration in milliseconds
 * @returns {string} Formatted duration (e.g., "2m 15s" or "45s")
 */
export function formatDuration(ms) {
  const totalSec = Math.max(0, Math.round(Number(ms || 0) / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/**
 * Normalizes text for GitHub Flavored Markdown rendering
 * Handles escaped newlines, table delimiters, and code fences
 * @param {string} text - Raw markdown text
 * @returns {string} Normalized markdown
 */
// --- Markdown line classifiers ---

const isTableDelimiter = (l) =>
  /^\s*\|?\s*:?-{1,}\s*:?(\s*\|\s*:?-{1,}\s*:?)+\s*\|?\s*$/.test(l);

const isTableRow = (l) => l.trimStart().startsWith('|');

const isHeading = (l) => /^\s*#{1,6}\s/.test(l);

const countPipes = (l) => (l.match(/\|/g) || []).length;

const matchFenceStart = (l) => l.match(/^(\s*)(`{3,}|~{3,})/);

// --- Markdown line transforms ---

/**
 * Indents orphan delimiter lines (no valid header row above)
 * so they render as code instead of broken tables.
 */
function indentOrphanDelimiters(line, lines, i) {
  if (!isTableDelimiter(line)) return line;
  let k = i - 1;
  while (k >= 0 && lines[k].trim() === '') k--;
  const prev = k >= 0 ? lines[k] : '';
  const looksValid =
    prev.includes('|') && countPipes(prev) > 0 && countPipes(line) >= countPipes(prev);
  return looksValid ? line : `    ${line}`;
}

/**
 * Returns true if a blank line is needed before `line` to
 * separate it from the previous emitted line (block boundary).
 */
function needsBlankLineBefore(line, prevEmitted) {
  if (prevEmitted.trim() === '') return false;
  if (isHeading(line)) return true;
  if (isTableRow(line) && !isTableRow(prevEmitted) && !isTableDelimiter(prevEmitted)) return true;
  return false;
}

/**
 * Returns true if a blank line is needed after `line` to
 * separate it from the next raw line (end of table).
 */
function needsBlankLineAfter(line, nextRaw) {
  if (!isTableRow(line)) return false;
  if (nextRaw.trim() === '') return false;
  return !isTableRow(nextRaw) && !isTableDelimiter(nextRaw);
}

/**
 * Splits a line containing <answer-summary>/<answer-detail> tags so each tag
 * sits on its own line surrounded by blank lines. Without a blank line after
 * an opening tag, CommonMark treats the tag plus the following text as a
 * single raw HTML block, so markdown inside it (e.g. **bold**) never parses.
 * The model is instructed to leave blank lines but does not reliably comply.
 * Returns null when the line contains no answer tag (no expansion needed).
 */
function expandAnswerTagLines(line) {
  if (!/<\/?answer-(summary|detail)>/.test(line)) return null;
  const out = [];
  line.split(/(<\/?answer-(?:summary|detail)>)/g).forEach((part) => {
    const trimmed = part.trim();
    if (trimmed === '') return;
    out.push('', trimmed, '');
  });
  return out;
}

export function normalizeForGfm(text) {
  const s = String(text || '')
    .replace(/\\n/g, '\n')
    .replace(/\r\n?/g, '\n');
  const lines = s.split('\n');

  let inFence = false;
  let activeFenceChar = '';
  let activeFenceLen = 0;
  const result = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Track code fences — skip transforms inside them
    const m = matchFenceStart(line);
    if (m) {
      const fenceChar = m[2][0];
      const len = m[2].length;
      if (!inFence) {
        inFence = true;
        activeFenceChar = fenceChar;
        activeFenceLen = len;
      } else if (
        line.trim().startsWith(activeFenceChar.repeat(activeFenceLen))
      ) {
        inFence = false;
        activeFenceChar = '';
        activeFenceLen = 0;
      }
      result.push(line);
      continue;
    }
    if (inFence) {
      result.push(line);
      continue;
    }

    // Ensure answer-summary/answer-detail tags sit on their own line with
    // blank lines around them, so the enclosed markdown renders correctly
    const expanded = expandAnswerTagLines(line);
    if (expanded) {
      result.push(...expanded);
      continue;
    }

    line = indentOrphanDelimiters(line, lines, i);

    const prevEmitted = result.length > 0 ? result[result.length - 1] : '';
    const nextRaw = i < lines.length - 1 ? lines[i + 1] : '';

    if (needsBlankLineBefore(line, prevEmitted)) result.push('');
    result.push(line);
    if (needsBlankLineAfter(line, nextRaw)) result.push('');
  }

  return result.join('\n');
}

/**
 * Determines if message text needs top spacing before markdown content
 * Skips extra spacing for ATX headings (# ...)
 * @param {string} text - Message text
 * @returns {boolean} True if top spacing is needed
 */
export function needsTopSpacer(text) {
  const t = String(text || '').replace(/^\s+/, '');
  // If starts with a Markdown ATX heading (# ...), we skip extra spacing
  return !t.startsWith('#');
}
